import { Component, computed, Injector, Input, OnInit, signal } from '@angular/core';
import _ from 'lodash';
import { BaseModalComponent, EnumValues } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { Roll } from '@app/models/knitting/roll';
import { Fabric } from '@app/models/order/work-order';
import { FabricProxyService } from '@app/proxies/item/fabric.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { EnumService } from '@app/services/enum.service';
import { KnittingUtil } from '@app/utilities/knitting-util';

export interface NeededStructureRollsPC {
  idWorkOrder: string;
  idProductionCard: string;
}

@Component({
  selector: 'app-assign-rolls-to-pc',
  templateUrl: `./assign-rolls-to-pc.component.html`,
  styleUrl: './assign-rolls-to-pc.component.scss',
})
export class AssignRollsToPcComponent extends BaseModalComponent<NeededStructureRollsPC | null> implements OnInit {
  @Input() structureRollsPC: NeededStructureRollsPC | null = null;
  @Input() selectedRolls: Roll[] = [];
  @Input() productionCardWeightInLbs = 0;
  @Input() productionCardNumber = '';

  isLoading = signal(0);
  rollsAvailable = signal<Roll[]>([]);
  fabricsAvailable = signal<Fabric[]>([]);
  rollsSelected = signal<Roll[]>([]);

  totalWeightOfRollsSelected = computed(() =>
    this.rollsSelected().reduce((total, roll) => {
      const timestamps = roll.stationTimeStamps ?? [];
      const mostRecent = timestamps.length > 0 ? timestamps[timestamps.length - 1] : undefined;
      return total + (mostRecent?.weightInLbs ?? 0);
    }, 0),
  );

  fabricFormEnums: EnumValues = {};

  constructor(
    injector: Injector,
    private rollProxiesService: RollProxyService,
    private productionCardsProxyService: ProductionCardsProxyService,
    private fabricsProxyService: FabricProxyService,
    private enumService: EnumService,
  ) {
    super(injector);
    this.fabricFormEnums = this.enumService.getEnum('FabricForm', 'itemEnums').values;
  }

  ngOnInit(): void {
    this.getRolls();
  }

  getRollTimeStamp = KnittingUtil.getRollTimeStamp;

  private getRolls() {
    if (!_.isNil(this.structureRollsPC)) {
      this.isLoading.update((n) => n + 1);
      this.rollProxiesService.getRollsByWorkOrder(this.structureRollsPC!.idWorkOrder, true).subscribe({
        next: ({ data }) => {
          this.isLoading.update((n) => n - 1);
          if (!_.isNil(data)) {
            this.rollsAvailable.set([...data, ...this.selectedRolls]);
            this.rollsSelected.set(this.selectedRolls);
            const itemsCodes = data.map((roll) => roll.expectedFabricCode!);
            this.getFabricByItemCodes([...new Set(itemsCodes)]);
          }
        },
        error: () => {
          this.isLoading.update((n) => n - 1);
          GlobalMessageUtil.warn('Warning', 'All rolls have been assigned to production cards');
          if (!_.isEmpty(this.selectedRolls)) {
            this.rollsAvailable.set(this.selectedRolls);
            this.rollsSelected.set(this.selectedRolls);
            const itemsCodes = this.selectedRolls.map((roll) => roll.expectedFabricCode!);
            this.getFabricByItemCodes([...new Set(itemsCodes)]);
          }
        },
      });
    } else {
      GlobalMessageUtil.error('Error', 'Dont have selected work order to get rolls');
      return;
    }
  }

  private getFabricByItemCodes(itemsCodes: string[]) {
    this.isLoading.update((n) => n + 1);
    return this.fabricsProxyService.batchFabrics(itemsCodes).subscribe({
      next: ({ data }) => {
        this.isLoading.update((n) => n - 1);
        if (!_.isNil(data)) {
          console.log(data);
          this.fabricsAvailable.set(data);
        }
      },
      error: (error) => {
        this.isLoading.update((n) => n - 1);
        console.error(error);
      },
    });
  }

  onClose() {
    this.close({
      data: null,
      action: 'close',
    });
  }

  pushRollsForAssign(newRoll: Roll) {
    const match = this.rollsSelected().find((roll) => roll.id === newRoll.id);
    if (match) {
      GlobalMessageUtil.warn('Warning', 'Roll already selected');
      return;
    }
    this.rollsSelected.update((rolls) => [...rolls, newRoll]);
  }

  deleteRollOfList(rollToDelete: Roll) {
    const match = this.rollsSelected().find((roll) => roll.id === rollToDelete.id);

    if (!match) {
      GlobalMessageUtil.warn('Warning', 'Roll not selected');
      return;
    }

    this.rollsSelected.update((rolls) => rolls.filter((r) => r.id !== rollToDelete.id));
  }

  isRollSelected(roll: Roll): boolean {
    return this.rollsSelected().some((r) => r.id === roll.id);
  }

  assignRolls() {
    const rollNumbers: string[] = this.rollsSelected().map((roll) => roll.number ?? '');

    console.log(rollNumbers);

    if (rollNumbers && rollNumbers.length > 0) {
      this.isLoading.set(1);
      this.productionCardsProxyService.assignRollsToProductionCard(this.structureRollsPC!.idProductionCard, rollNumbers).subscribe({
        next: (response) => {
          if (response.success) {
            this.isLoading.set(0);
            GlobalMessageUtil.success('Success', 'Rolls assigned successfully');
            this.onClose();
          }
        },
        error: (error) => {
          this.isLoading.set(0);
          console.error(error);
          return;
        },
      });
    } else {
      GlobalMessageUtil.warn('Warning', 'No rolls selected');
      return;
    }
  }

  fetchFabricTubularOrOpenWith(fabricCode: string): string {
    const fabric = this.fabricsAvailable().find((f) => f.fabricCode === fabricCode);
    if (!fabric) {
      throw new Error('Fabric not found in work order');
    }
    const enumValue = this.fabricFormEnums[fabric.fabricForm!];
    return enumValue ? enumValue : ' ';
  }
}
